import { InlineNotification, TextArea } from '@carbon/react';
import classNames from 'classnames';

import { format0 } from 'utils/number';

import type { BulkFailureEntry, SlotInterestRow } from '../types';
import { isEligibleForBulkAm, rowKey } from '../utils';
import styles from './bulk-content.module.scss';

interface BulkContentProps {
  rows: SlotInterestRow[];
  /** Set when a 207 returned; the list filters down to these. */
  failures: BulkFailureEntry[] | null;
  /** Optional supporting note applied to every bulk-certified slot. */
  note: string;
  onNoteChange: (note: string) => void;
  /** Disables the note field while a submit is in flight. */
  submitting: boolean;
}

/**
 * Bulk mode body: an info banner (bulk confirms are Allocation-Manager only) +
 * a scrollable list of the selected slots. Ineligible slots stay in the list
 * but muted with a reason; after a partial failure (207) the list filters to
 * just the failed slots and shows each BE failure message verbatim.
 *
 * @param {BulkContentProps} props - Component props.
 * @return {JSX.Element} The bulk panel body.
 */
export const BulkContent = ({
  rows,
  failures,
  note,
  onNoteChange,
  submitting,
}: BulkContentProps): JSX.Element => {
  const failureMessages =
    failures === null
      ? null
      : new Map(
          failures.map((failure) => [failure.workspaceSlotId, failure.message])
        );

  const displayRows =
    failureMessages === null
      ? rows
      : rows.filter((row) => failureMessages.has(row.workspaceSlotId));

  return (
    <div className={styles.section}>
      <InlineNotification
        className={styles.banner}
        kind="info"
        lowContrast
        hideCloseButton
        title="Bulk certifies use Allocation Manager"
        subtitle="To use Division Order or Custom Interest, open and certify each slot individually."
      />
      {failures !== null && (
        <InlineNotification
          className={styles.banner}
          kind="error"
          lowContrast
          hideCloseButton
          title={`${format0(failures.length)} of ${format0(
            rows.length
          )} slots failed to confirm.`}
        />
      )}
      <h3 className={styles.listHeading}>Slots being updated</h3>
      <ul className={styles.list}>
        {displayRows.map((row) => {
          let helper: string | undefined;
          let muted = false;
          if (failureMessages !== null) {
            helper = failureMessages.get(row.workspaceSlotId);
          } else if (!isEligibleForBulkAm(row)) {
            muted = true;
            helper = row.deactivated ? 'Deactivated' : 'No AM values';
          }
          return (
            <li
              key={rowKey(row)}
              className={classNames(styles.listItem, {
                [styles.listItemMuted]: muted,
              })}
            >
              <span className={styles.slotMain}>
                {row.slotId} — {row.wellName}
              </span>
              {helper && <span className={styles.helperText}>{helper}</span>}
            </li>
          );
        })}
      </ul>
      {failures === null && (
        <TextArea
          id="bulk-supporting-note"
          labelText="Supporting note (optional)"
          placeholder="Document the reasoning for this batch…"
          value={note}
          disabled={submitting}
          onChange={(event) => onNoteChange(event.target.value)}
        />
      )}
    </div>
  );
};
